import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { t } from "../../i18n";
import "./ConnectScreen.css";

// The full-screen frame of "Connect your bank" (founder, 2026-10-19: "this is a
// very important screen regarding payments … more substantial than that little
// pop-up"; gallery .claude/1.5.2-connect-bank-fullscreen.html). One frame for
// every step, in sign-up and behind every money button:
//
//   top     Back (when there is a step to go back to) · step bar · Close
//   body    scrolls; `center` puts short content in the middle of the screen
//   foot    the step's buttons, pinned above the home bar
//
// On <body>, above everything (the Profile drawer and the sheets that open it
// have their own stacking contexts). Escape closes it on a computer.

const BackIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

export function ConnectScreen(props: {
  /** How far along the set-up is, 0–100. */
  progress: number;
  onClose: () => void;
  onBack?: () => void;
  /** id of the step's title, for the dialog's name. */
  labelledBy?: string;
  /** Busy (a server call is out): Back and Close wait. */
  busy?: boolean;
  children: React.ReactNode;
}) {
  const { onClose, busy } = props;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  return createPortal(
    <div className="cxs" role="dialog" aria-modal="true" aria-labelledby={props.labelledBy || "cx-title"}>
      <div className="cxs-col">
        <div className="cxs-top">
          {props.onBack ? (
            <button type="button" className="cxs-iconbtn" onClick={props.onBack} disabled={busy} aria-label={t("connectBankSheet.back")}><BackIcon /></button>
          ) : <span />}
          <span className="cxs-prog" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, props.progress))}%` }} /></span>
          <button type="button" className="cxs-iconbtn" onClick={onClose} disabled={busy} aria-label={t("connectBankSheet.close")}><CloseIcon /></button>
        </div>
        {props.children}
      </div>
    </div>,
    document.body
  );
}

/** The step's scrolling content. `center`: short content sits mid-screen. */
export function ConnectBody({ center, children }: { center?: boolean; children: React.ReactNode }) {
  return (
    <div className={`cxs-body${center ? " cxs-body-center" : ""}`}>
      {center ? <div className="cxs-center">{children}</div> : children}
    </div>
  );
}

/** The step's buttons, pinned at the bottom. */
export function ConnectFoot({ children }: { children: React.ReactNode }) {
  return <div className="cxs-foot">{children}</div>;
}

/** The purple primary button; the chevron says "forward", so a closing button goes without it. */
export function PrimaryButton(props: { onClick?: () => void; type?: "button" | "submit"; form?: string; disabled?: boolean; busy?: boolean; noChevron?: boolean; children: React.ReactNode }) {
  return (
    <button type={props.type || "button"} form={props.form} className="cxs-primary" onClick={props.onClick} disabled={props.disabled} aria-busy={props.busy || undefined}>
      <span>{props.children}</span>
      {!props.noChevron && (
        <svg className="cxs-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
      )}
    </button>
  );
}

export function SecondaryButton(props: { onClick?: () => void; disabled?: boolean; children: React.ReactNode }) {
  return <button type="button" className="cxs-secondary" onClick={props.onClick} disabled={props.disabled}>{props.children}</button>;
}
